import { useOrg } from '@/contexts/OrgContext';
import { track } from '@/lib/analytics';
import { useAuth0 } from '@auth0/auth0-react';
import { AnalyticsEvents } from '@beacon/analytics-events';
import {
  Button,
  Eyebrow,
  ExpandingInput,
  Icon,
  type IconType,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  useToast,
} from '@beacon/ui/components';
import { useState } from 'react';

export type SupportMode = 'support' | 'bug';

export interface SupportAttribution {
  source?: string;
  promoId?: string;
}

const COPY: Record<
  SupportMode,
  {
    tag: string;
    title: string;
    icon: IconType;
    placeholder: string;
    text: string;
  }
> = {
  support: {
    tag: '// SUPPORT',
    title: 'Contact support',
    icon: 'customer-service-2-line',
    placeholder: 'What do you need help with?',
    text: 'Reach the BreakLine team directly. We reply within one business day.',
  },
  bug: {
    tag: '// BUG_REPORT',
    title: 'Report a bug',
    icon: 'error-warning-line',
    placeholder:
      'Walk us through what went wrong. What did you expect, and what happened instead? Steps to reproduce help too.',
    text: 'Describe the issue. Page context is automatically attached.',
  },
};

export function SupportSheet({
  open,
  mode,
  attribution,
  onOpenChange,
}: {
  open: boolean;
  mode: SupportMode;
  attribution?: SupportAttribution;
  onOpenChange: (open: boolean) => void;
}) {
  const { success } = useToast();
  const { currentOrg } = useOrg();
  const { user } = useAuth0();
  const [message, setMessage] = useState('');

  const copy = COPY[mode];

  function handleClose(next: boolean) {
    if (!next) setMessage('');
    onOpenChange(next);
  }

  function handleSubmit() {
    const trimmed = message.trim();

    if (!trimmed) return;

    track(mode === 'bug' ? AnalyticsEvents.BUG_REPORTED : AnalyticsEvents.SUPPORT_REQUESTED, {
      type: mode,
      message: trimmed,
      url: window.location.href,
      userAgent: navigator.userAgent,
      orgSlug: currentOrg?.tenantSlug ?? null,
      userEmail: user?.email ?? null,
      source: attribution?.source ?? null,
      promoId: attribution?.promoId ?? null,
    });

    success(mode === 'bug' ? 'Thank you for submitting a bug report.' : 'Your support request was sent.');
    handleClose(false);
  }

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-[440px]">
        <div className="border-stroke-1 flex items-center gap-3 border-b px-5 py-3.5">
          <Icon symbol={copy.icon} className="text-primary-9 h-4 w-4" />
          <Eyebrow className="text-primary-9 text-2xs">{copy.tag}</Eyebrow>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => handleClose(false)}
            aria-label="Close"
            className="text-text-3 hover:text-text-1 p-1"
          >
            <Icon symbol="close-line" className="text-lg" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 px-5 py-5">
          <div>
            <SheetTitle className="text-text-1 text-lg font-semibold">{copy.title}</SheetTitle>
            <SheetDescription className="text-text-3 mt-1 text-xs">{copy.text}</SheetDescription>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <ExpandingInput
              value={message}
              onChange={setMessage}
              placeholder={copy.placeholder}
              className="min-h-[160px]"
            />
          </div>
        </div>

        <div className="border-stroke-1 flex items-center justify-end gap-2 border-t px-5 py-3.5">
          <Button variant="outline" size="sm" mono type="button" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" mono type="button" onClick={handleSubmit} disabled={!message.trim()}>
            Send
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
